import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn, SignUp, useAuth } from '@clerk/clerk-react';
import { forgetAllDeactivatedUnits } from '@/lib/flowDeactivatedUnits';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { RouteRecord } from 'vite-react-ssg';
// IMPORTANT: HelmetProvider is intentionally NOT imported here. vite-react-ssg
// wraps the tree in its own HelmetProvider with a server-side context that's
// the only one `extractHelmet` reads from during SSG. If we mount our own
// provider above it, every <Helmet> in pages registers to the inner context
// and the outer extraction sees nothing — every page ends up with the
// fallback title from index.html. Letting vite-react-ssg own the provider
// is what makes per-route titles work in the prerendered HTML.
import { DashboardLayout } from '@/components/layout';
import { UserSyncProvider, SupabaseProvider, SentryUserProvider, AnalyticsProvider } from '@/components/providers';
import { CookieConsentBanner } from '@/components/ui';
import { AdminGuard } from '@/components/admin';
import { RequireSubscription } from '@/components/subscription';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { TokenRefreshInvalidator } from '@/components/providers/TokenRefreshInvalidator';
import {
  MarketingGate,
  AppGate,
  DynamicLandingRoute,
  TeaserGate,
} from '@/components/maintenance/MaintenanceGates';
import { SUPPORTED_LANGS, type BlogLang } from '@/lib/blogLangs';
import { collectBlogSlugs } from '@/blog-ssg/collectSlugs';
import '@/i18n';

// --- Page imports ---
// Public/marketing pages are eager-imported so react-helmet-async can commit
// titles & meta tags synchronously during SSG. Lazy()-wrapped components
// don't resolve in time during static rendering — Helmet collection sees
// the Suspense fallback (no Helmet calls) and falls back to index.html
// defaults, which would defeat the whole point of prerendering.
// Bundle cost: marketing pages are small (~30–60kb each); they tree-shake
// fine and the per-route prerendered HTML files load only their own chunks.
import PricingPage from '@/pages/PricingPage';
import BetaLandingPage from '@/pages/BetaLandingPage';
import MaintenancePage from '@/pages/MaintenancePage';
import PrivacyPage from '@/pages/PrivacyPage';
import TermsPage from '@/pages/TermsPage';
import CookiesPage from '@/pages/CookiesPage';
import ContactPage from '@/pages/ContactPage';
import AboutPage from '@/pages/AboutPage';
import LoginSupportPage from '@/pages/LoginSupportPage';
import BlogPage from '@/pages/BlogPage';
import FeaturesPage from '@/pages/FeaturesPage';
import ZaIznajmljivacePage from '@/pages/ZaIznajmljivacePage';
import ZaAgencijePage from '@/pages/ZaAgencijePage';
import ZaVilePage from '@/pages/ZaVilePage';
import NotFoundPage from '@/pages/NotFoundPage';

// Demo portal — eager-imported too, just so the SSG bundle doesn't
// have any Suspense boundaries above pages that emit <Helmet>. (The
// page itself is excluded from prerendering via the includedRoutes
// filter in vite.config.ts.)
import DemoGuestPortalPage from '@/pages/DemoGuestPortalPage';

// Guest pages
const GuestAccessPage = lazy(() => import('@/pages/GuestAccessPage'));
const GuestPortalPage = lazy(() => import('@/pages/GuestPortalPage'));

// Dashboard pages
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const PropertiesPage = lazy(() => import('@/pages/PropertiesPage'));
const PropertyDetailPage = lazy(() => import('@/pages/PropertyDetailPage'));
const PropertyFormPage = lazy(() => import('@/pages/PropertyFormPage'));
const UnitsPage = lazy(() => import('@/pages/UnitsPage'));
const UnitDetailPage = lazy(() => import('@/pages/UnitDetailPage'));
const UnitFormPage = lazy(() => import('@/pages/UnitFormPage'));
const BookingsPage = lazy(() => import('@/pages/BookingsPage'));
const BookingFormPage = lazy(() => import('@/pages/BookingFormPage'));
const BookingDetailPage = lazy(() => import('@/pages/BookingDetailPage'));

const MessagesPage = lazy(() => import('@/pages/MessagesPage'));
const SmartInboxPage = lazy(() => import('@/pages/SmartInboxPage'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const AccountSettingsPage = lazy(() => import('@/pages/AccountSettingsPage'));
const QuickStartPage = lazy(() => import('@/pages/QuickStartPage'));
const PaymentMethodsPage = lazy(() => import('@/pages/PaymentMethodsPage'));
const LocalGuidePage = lazy(() => import('@/pages/LocalGuidePage'));
const TeamPage = lazy(() => import('@/pages/TeamPage'));
const HostUpdatesPage = lazy(() => import('@/pages/HostUpdatesPage'));
const CommunityPage = lazy(() => import('@/pages/CommunityPage'));
const ThreadDetailPage = lazy(() => import('@/pages/ThreadDetailPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const SupportPage = lazy(() => import('@/pages/SupportPage'));
const ReviewsPage = lazy(() => import('@/pages/ReviewsPage'));
const TrashPage = lazy(() => import('@/pages/TrashPage'));

// Calendar pages
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const CalendarSettingsPage = lazy(() => import('@/pages/CalendarSettingsPage'));
const EmbedCalendarPage = lazy(() => import('@/pages/EmbedCalendarPage'));

// Admin pages
const AdminDashboardPage = lazy(() => import('@/pages/AdminDashboardPage'));
const AdminUsersPage = lazy(() => import('@/pages/AdminUsersPage'));
const AdminUserDetailPage = lazy(() => import('@/pages/AdminUserDetailPage'));
const AdminAnalyticsPage = lazy(() => import('@/pages/AdminAnalyticsPage'));
const AdminSubscribersPage = lazy(() => import('@/pages/AdminSubscribersPage'));
const AdminSettingsPage = lazy(() => import('@/pages/AdminSettingsPage'));
const AdminInvitePage = lazy(() => import('@/pages/AdminInvitePage'));
const AdminSupportPage = lazy(() => import('@/pages/AdminSupportPage'));
const AdminTicketDetailPage = lazy(() => import('@/pages/AdminTicketDetailPage'));
const AdminSystemHealthPage = lazy(() => import('@/pages/AdminSystemHealthPage'));

// Get Clerk publishable key
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || 'pk_test_placeholder';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 3000),
      refetchOnMount: true,
    },
  },
});

// Loading spinner for Suspense fallback
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</span>
      </div>
    </div>
  );
}

// Legacy /blog/:slug → /hr/blog/:slug.
function BlogSlugRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/hr/blog/${slug}`} replace />;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

/**
 * /sign-up — Clerk's sign-up form on our own domain. Admin invitations link
 * here with `?__clerk_ticket=…`; the component reads the ticket from the URL,
 * so the query string must survive (no redirect). Not behind the coming-soon
 * teaser: an invited host has to be able to create the account.
 */
function SignUpRoute() {
  return (
    <>
      <SignedIn>
        <Navigate to="/dashboard" replace />
      </SignedIn>
      <SignedOut>
        <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] p-4">
          <SignUp routing="path" path="/sign-up" fallbackRedirectUrl="/dashboard" />
        </div>
      </SignedOut>
    </>
  );
}

/**
 * Clerk's own "Delete account" (UserButton → Manage account → Danger zone)
 * would delete the Clerk user at once — no 30-day undo, Stripe renewal left
 * running. Account deletion goes through /dashboard/account only.
 */
const clerkAppearance = {
  elements: {
    profileSection__danger: { display: 'none' },
  },
};

/** Signed out → forget per-user conveniences kept in this tab. */
function SignedOutCleanup() {
  const { isLoaded, isSignedIn } = useAuth();
  useEffect(() => {
    if (isLoaded && !isSignedIn) forgetAllDeactivatedUnits();
  }, [isLoaded, isSignedIn]);
  return null;
}

/* ─── Root Layout ───
 * Wraps every route in the provider stack. vite-react-ssg renders this at
 * build time too, so providers must be SSR-safe (ClerkProvider tolerates
 * no-session; QueryClient is fine; HelmetProvider is the whole point).
 * Sentry/PWA init lives in main.tsx and is gated behind `import.meta.env.PROD`
 * + `typeof window !== 'undefined'` so it doesn't trip during SSG.
 */
function RootLayout() {
  if (!clerkPubKey || clerkPubKey === 'pk_test_placeholder') {
    // Only render the missing-key warning in the browser. During SSG (no
    // window) we just render <Outlet/> so the prerender succeeds even if
    // the build env doesn't have a Clerk key.
    if (typeof window !== 'undefined') {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))]">
          <div className="text-center p-8 max-w-md">
            <h1 className="text-2xl font-bold mb-4">Clerk nije konfiguriran</h1>
            <p className="text-[hsl(var(--muted-foreground))] mb-4">
              Molimo dodajte vaš Clerk publishable key u .env datoteku:
            </p>
            <code className="block bg-[hsl(var(--muted))] p-4 rounded text-sm text-left">
              VITE_CLERK_PUBLISHABLE_KEY=pk_test_...
            </code>
          </div>
        </div>
      );
    }
  }

  return (
    <ErrorBoundary>
      <ClerkProvider publishableKey={clerkPubKey} appearance={clerkAppearance}>
        <SupabaseProvider>
          <SentryUserProvider>
            <QueryClientProvider client={queryClient}>
              <UserSyncProvider>
                <TokenRefreshInvalidator />
                <SignedOutCleanup />
                <AnalyticsProvider />
                <CookieConsentBanner />
                {/* Suspense still needed for dashboard/admin routes that
                    remain lazy() (they're not prerendered) — but all the
                    public/blog routes load eagerly so their <Helmet> tags
                    get collected synchronously during SSG. */}
                <Suspense fallback={<PageLoader />}>
                  <Outlet />
                </Suspense>
              </UserSyncProvider>
            </QueryClientProvider>
          </SentryUserProvider>
        </SupabaseProvider>
      </ClerkProvider>
    </ErrorBoundary>
  );
}

/* ─── Blog routes ───
 * Built dynamically so `getStaticPaths` enumerates the slugs that exist on
 * disk for each language. This is what tells vite-react-ssg to prerender
 * /hr/blog/fiskalizacija-2-0-..., /en/blog/fiskalizacija-2-0-..., etc.
 */
function buildBlogRoutes(): RouteRecord[] {
  const blogRoutes: RouteRecord[] = [];

  for (const lang of SUPPORTED_LANGS) {
    blogRoutes.push({
      path: `${lang}/blog`,
      element: <MarketingGate><BlogPage langOverride={lang as BlogLang} /></MarketingGate>,
    });
    blogRoutes.push({
      path: `${lang}/blog/:slug`,
      // Lazy: the article page + all article bodies load only on article URLs
      // (they used to ship with the app shell — 1.5 MB on every page).
      lazy: () => import('@/pages/BlogPostRoute'),
      getStaticPaths: () => collectBlogSlugs(lang as BlogLang).map((s) => `${lang}/blog/${s}`),
    });
  }

  return blogRoutes;
}

/* ─── Routes export ───
 * vite-react-ssg consumes this at build time to enumerate prerenderable
 * routes. Auth-gated routes (dashboard/admin/guest portal) are included
 * here so client-side routing works after hydration, but vite-react-ssg's
 * default `includedRoutes` filter drops dynamic `:id` segments — and we
 * use `ssgOptions.includedRoutes` in vite.config.ts to also drop the
 * /dashboard, /admin, /guest, /embed, /maintenance, /support trees from
 * prerendering. Those routes need fresh auth/data and aren't indexable.
 */
export const routes: RouteRecord[] = [
  {
    path: '/',
    Component: RootLayout,
    children: [
      // Root: switches between LandingPage / ComingSoon / Beta based on settings
      {
        index: true,
        element: <MarketingGate><DynamicLandingRoute /></MarketingGate>,
      },

      // Public marketing routes
      { path: 'beta', element: <MarketingGate><BetaLandingPage /></MarketingGate> },
      {
        path: 'pricing',
        element: <MarketingGate><TeaserGate variant="pricing"><PricingPage /></TeaserGate></MarketingGate>,
      },
      { path: 'privacy', element: <MarketingGate><PrivacyPage /></MarketingGate> },
      { path: 'terms', element: <MarketingGate><TermsPage /></MarketingGate> },
      { path: 'cookies', element: <MarketingGate><CookiesPage /></MarketingGate> },
      { path: 'contact', element: <MarketingGate><ContactPage /></MarketingGate> },
      {
        path: 'about',
        element: <MarketingGate><TeaserGate variant="about"><AboutPage /></TeaserGate></MarketingGate>,
      },
      { path: 'demo', element: <MarketingGate><DemoGuestPortalPage /></MarketingGate> },

      // Legacy blog redirects (not prerendered — redirected at the edge or client)
      { path: 'blog', element: <Navigate to="/hr/blog" replace /> },
      { path: 'blog/:slug', element: <BlogSlugRedirect /> },

      // Multilang blog (prerendered with getStaticPaths)
      ...buildBlogRoutes(),

      {
        path: 'features',
        element: <MarketingGate><TeaserGate variant="features"><FeaturesPage /></TeaserGate></MarketingGate>,
      },
      { path: 'za-iznajmljivace', element: <MarketingGate><ZaIznajmljivacePage /></MarketingGate> },
      { path: 'za-agencije', element: <MarketingGate><ZaAgencijePage /></MarketingGate> },
      { path: 'za-vile', element: <MarketingGate><ZaVilePage /></MarketingGate> },

      { path: 'maintenance', element: <MaintenancePage /> },
      { path: 'support', element: <AppGate><LoginSupportPage /></AppGate> },

      // Guest Portal routes (PWA) — never prerendered (token-based)
      { path: 'guest', element: <AppGate><GuestAccessPage /></AppGate> },
      { path: 'guest/:token', element: <AppGate><GuestAccessPage /></AppGate> },
      { path: 'guest/portal/:token', element: <AppGate><GuestPortalPage /></AppGate> },

      // Embed Calendar (iframe target on hosts' own websites — never
      // prerendered, no maintenance gate: a maintenance page inside someone
      // else's site helps nobody; if the data can't load the calendar says so)
      { path: 'embed/calendar/:unitId', element: <EmbedCalendarPage /> },

      // Protected dashboard routes — never prerendered (auth-gated)
      {
        path: 'dashboard',
        element: (
          <AppGate>
            <ProtectedRoute>
              <RequireSubscription>
                <DashboardLayout />
              </RequireSubscription>
            </ProtectedRoute>
          </AppGate>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'properties', element: <PropertiesPage /> },
          { path: 'properties/new', element: <PropertyFormPage /> },
          { path: 'properties/:id', element: <PropertyDetailPage /> },
          { path: 'properties/:id/edit', element: <PropertyFormPage /> },
          { path: 'properties/:propertyId/units/new', element: <UnitFormPage /> },
          { path: 'properties/:propertyId/units/:unitId', element: <UnitDetailPage /> },
          { path: 'properties/:propertyId/units/:unitId/edit', element: <UnitFormPage /> },
          { path: 'units', element: <UnitsPage /> },
          { path: 'bookings', element: <BookingsPage /> },
          { path: 'bookings/new', element: <BookingFormPage /> },
          { path: 'bookings/:id', element: <BookingDetailPage /> },
          { path: 'bookings/:id/edit', element: <BookingFormPage /> },
          { path: 'calendar', element: <CalendarPage /> },
          { path: 'calendar/:unitId/settings', element: <CalendarSettingsPage /> },
          { path: 'messages', element: <MessagesPage /> },
          { path: 'smart-inbox', element: <SmartInboxPage /> },
          { path: 'billing', element: <BillingPage /> },
          { path: 'account', element: <AccountSettingsPage /> },
          { path: 'quick-start', element: <QuickStartPage /> },
          { path: 'payment-methods', element: <PaymentMethodsPage /> },
          { path: 'local-guide', element: <LocalGuidePage /> },
          { path: 'host-updates', element: <HostUpdatesPage /> },
          { path: 'community', element: <CommunityPage /> },
          { path: 'community/:threadId', element: <ThreadDetailPage /> },
          { path: 'team', element: <TeamPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'reviews', element: <ReviewsPage /> },
          { path: 'trash', element: <TrashPage /> },
          { path: 'support', element: <SupportPage /> },

          // Admin routes
          { path: 'admin', element: <AdminGuard><AdminDashboardPage /></AdminGuard> },
          { path: 'admin/users', element: <AdminGuard><AdminUsersPage /></AdminGuard> },
          { path: 'admin/users/:userId', element: <AdminGuard><AdminUserDetailPage /></AdminGuard> },
          { path: 'admin/analytics', element: <AdminGuard><AdminAnalyticsPage /></AdminGuard> },
          { path: 'admin/subscribers', element: <AdminGuard><AdminSubscribersPage /></AdminGuard> },
          { path: 'admin/settings', element: <AdminGuard><AdminSettingsPage /></AdminGuard> },
          { path: 'admin/invite', element: <AdminGuard><AdminInvitePage /></AdminGuard> },
          { path: 'admin/support', element: <AdminGuard><AdminSupportPage /></AdminGuard> },
          { path: 'admin/support/:ticketId', element: <AdminGuard><AdminTicketDetailPage /></AdminGuard> },
          { path: 'admin/system-health', element: <AdminGuard><AdminSystemHealthPage /></AdminGuard> },
        ],
      },

      // No sign-in page of our own (Clerk's modal handles that) — keep sending
      // it to the homepage as the old catch-all did.
      { path: 'sign-in', element: <Navigate to="/" replace /> },
      // Sign-up is a real page: invitation links carry a Clerk ticket in the
      // query string (`/*` for Clerk's own steps like verify-email-address).
      { path: 'sign-up/*', element: <AppGate><SignUpRoute /></AppGate> },

      // Catch-all: a real "not found" page with noindex (see NotFoundPage)
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

// Default export kept for backwards compat (anything that still imports
// `App` as a component gets a no-op — runtime uses ViteReactSSG with the
// `routes` export above).
export default function App() {
  return null;
}
